import axios from "axios";
import { API_BASE } from "@/config";

// Axios instance — base URL preserved from existing env config.
export const http = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

// Attach bearer token from session on every request.
http.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("sa_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

/* ----------------------------- AUTH ----------------------------- */
export const authApi = {
  // POST /api/superadmin/auth/login
  async login({ email, password }) {
    const { data } = await http.post("/api/superadmin/auth/login", { email, password });
    return data;
  },
  // POST /api/superadmin/auth/verify-otp
  async verifyOtp({ email, otp }) {
    const { data } = await http.post("/api/superadmin/auth/verify-otp", { email, otp });
    return data;
  },
};

/* --------------------------- DASHBOARD --------------------------- */
export const dashboardApi = {
  // GET /api/superadmin/dashboard-summary
  async summary() {
    const { data } = await http.get("/api/superadmin/dashboard-summary");
    return data;
  },
  // GET /api/superadmin/pending-clinics
  async pending() {
    const { data } = await http.get("/api/superadmin/pending-clinics");
    // Normalize response to an array — backend should return a list,
    // but some environments may wrap or return an object. Be defensive.
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.pending)) return data.pending;
    if (Array.isArray(data?.data)) return data.data;
    return [];
  },
  // PUT /api/superadmin/approve-clinic/:id
  async approve(id) {
    const { data } = await http.put(`/api/superadmin/approve-clinic/${id}`);
    return data;
  },
  // PUT /api/superadmin/hold-clinic/:id
  async hold(id) {
    const { data } = await http.put(`/api/superadmin/hold-clinic/${id}`);
    return data;
  },
  // PUT /api/superadmin/reject-clinic/:id
  async reject(id) {
    const { data } = await http.put(`/api/superadmin/reject-clinic/${id}`);
    return data;
  },
};

/* ---------------------------- CLINICS ---------------------------- */
export const clinicApi = {
  // GET /api/superadmin/clinics
  async list() {
    const { data } = await http.get("/api/superadmin/clinics");
    return data;
  },
  // GET /api/superadmin/clinics/:id
  async detail(id) {
    const { data } = await http.get(`/api/superadmin/clinics/${id}`);
    return data;
  },
  // PUT /api/superadmin/clinics/:id/status
  async updateStatus(id, status) {
    const { data } = await http.put(`/api/superadmin/clinics/${id}/status`, { status });
    return data;
  },
  // GET /api/superadmin/clinics/:id/history
  async history(id) {
    const { data } = await http.get(`/api/superadmin/clinics/${id}/history`);
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.timeline)) return data.timeline;
    if (Array.isArray(data?.history)) return data.history;
    return [];
  },
};

/* --------------------------- REVENUE ----------------------------- */
export const revenueApi = {
  // POST /api/superadmin/revenue/overview
  async overview() {
    const { data } = await http.post("/api/superadmin/revenue/overview", { type: "MONTH" });
    return data;
  },
  // GET /api/superadmin/subscribers
  async subscribers() {
    const { data } = await http.get("/api/superadmin/subscribers");
    return data;
  },
};

const DEMO_STATUS_MAP = {
  new: "PENDING",
  contacted: "HOLD",
  scheduled: "HOLD",
  completed: "DONE",
  rejected: "REJECTED",
  onforce: "HOLD",
};

/* ------------------------- DEMO REQUESTS ------------------------- */
export const demoApi = {
  // GET /api/demo/requests
  async list() {
    const { data } = await http.get("/api/demo/requests");
    return data;
  },
  // PUT /api/demo/requests/:id
  async updateStatus(id, status) {
    const normalizedStatus = DEMO_STATUS_MAP[status] || status?.toUpperCase();
    const { data } = await http.put(`/api/demo/requests/${id}`, { status: normalizedStatus });
    return data;
  },
  // PUT /api/demo/requests/:id (notes)
  async updateNotes(id, notes) {
    const { data } = await http.put(`/api/demo/requests/${id}`, { notes });
    return data;
  },
  async updateSchedule(id, schedule) {
    const { data } = await http.put(`/api/demo/requests/${id}`, schedule);
    return data;
  },
  async sendConfirmation(id) {
    const { data } = await http.post(`/api/demo/requests/${id}/confirmation`);
    return data;
  },
};

/* ---------------------- OWNER CUSTOM MESSAGING ---------------------- */
export const ownerMessagingApi = {
  async clinics() {
    const { data } = await http.get("/api/superadmin/owner-messaging/clinics");
    if (!Array.isArray(data)) throw new Error("The server returned an invalid clinic list.");
    return data;
  },
  async campaigns() {
    const { data } = await http.get("/api/superadmin/owner-messaging/campaigns");
    if (!Array.isArray(data)) throw new Error("The server returned an invalid campaign history.");
    return data;
  },
  async campaign(id) {
    const { data } = await http.get(`/api/superadmin/owner-messaging/campaigns/${id}`);
    return data;
  },
  async sendCampaign(campaign, idempotencyKey) {
    const { data } = await http.post("/api/superadmin/owner-messaging/campaigns", campaign, {
      headers: {
        "Idempotency-Key": idempotencyKey,
      },
    });
    return data;
  },
  async retryRecipient(campaignId, recipientId) {
    const { data } = await http.post(
      `/api/superadmin/owner-messaging/campaigns/${campaignId}/recipients/${recipientId}/retry`
    );
    return data;
  },
};
